// src/components/BracketGame.tsx
import React from 'react';
import { BracketGame as BracketGameType, WinnerSource } from '../types/types';
import { GameBreakdown } from '../utils/pickBreakdownUtils';
import { CircleCheck, Lock, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';

interface BracketGameProps {
  game: BracketGameType;
  knownWinnersWithMetadata: { team: string; source: WinnerSource }[];
  hoveredTeamId: string | null;
  handleTeamSelect: (gameId: number, team: string) => void;
  handleResetTeam: (gameId: number, teamToReset: string | undefined) => void;
  setHoveredTeamId: (id: string | null) => void;
  getTeamSeed: (teamName: string) => number;
  findSourceGameForTeam: (teamName: string | undefined, currentGameId: number) => number | null;
  isTeamFromKnownWinnerPath: (teamName: string, gameId: number, knownWinners: { team: string; source: WinnerSource }[], games: BracketGameType[]) => boolean;
  isTeamFromOfficialKnownWinnerPath: (teamName: string, gameId: number, knownWinners: { team: string; source: WinnerSource }[], games: BracketGameType[]) => boolean;
  games: BracketGameType[];
  compact?: boolean;
  compactTextClass?: string;
  // Breakdown mode props
  mode?: 'scenario' | 'breakdown';
  breakdown?: GameBreakdown;
  onBreakdownClick?: (gameId: number) => void;
  isSelectedForBreakdown?: boolean;
}

/**
 * Component to render a single game in the tournament bracket.
 */
const BracketGame: React.FC<BracketGameProps> = ({
  game,
  knownWinnersWithMetadata,
  hoveredTeamId,
  handleTeamSelect,
  handleResetTeam,
  setHoveredTeamId,
  getTeamSeed,
  findSourceGameForTeam,
  isTeamFromKnownWinnerPath,
  isTeamFromOfficialKnownWinnerPath,
  games,
  compact = false,
  compactTextClass,
  mode = 'scenario',
  breakdown,
  onBreakdownClick,
  isSelectedForBreakdown,
}) => {
  // Breakdown mode rendering
  if (mode === 'breakdown') {
    const topPicks = breakdown?.picks.slice(0, 2) || [];
    const remaining = Math.max(0, (breakdown?.picks.length || 0) - 2);
    const topPercentage = topPicks[0]?.percentage || 0;

    const consensusBorder = topPercentage >= 75
      ? 'border-l-emerald-500'
      : topPercentage >= 50
        ? 'border-l-amber-500'
        : topPercentage > 0
          ? 'border-l-red-400'
          : 'border-l-border';

    return (
      <div
        className={cn(
          "rounded-lg border bg-card cursor-pointer transition-all border-l-[3px]",
          compact ? "p-1" : "p-2.5",
          consensusBorder,
          breakdown?.isOfficial && "opacity-60",
          isSelectedForBreakdown && "ring-2 ring-primary shadow-md"
        )}
        onClick={() => onBreakdownClick?.(game.id)}
      >
        <div className={cn("flex flex-col", compact ? "gap-0.5" : "gap-1")}>
          {topPicks.length === 0 ? (
            <div className={cn(
              "text-muted-foreground italic px-1.5 py-0.5",
              compact ? compactTextClass || 'text-[14px]' : 'text-sm'
            )}>
              No picks
            </div>
          ) : (
            <>
              {topPicks.map((pick, idx) => (
                <div
                  key={pick.team}
                  className={cn(
                    "flex items-center rounded-md",
                    compact ? `gap-1 px-1.5 py-0.5 ${compactTextClass || 'text-[14px]'}` : 'gap-2 px-2.5 py-1.5 text-sm',
                    breakdown?.isOfficial && pick.team === breakdown.officialWinner && 'bg-teal/10'
                  )}
                >
                  <Badge variant="secondary" className={cn(
                    "justify-center rounded-sm py-0",
                    compact ? "min-w-[22px] px-1 text-xs" : "min-w-[28px] px-1.5 text-xs"
                  )}>
                    {getTeamSeed(pick.team)}
                  </Badge>
                  <span className="flex-1 truncate">{pick.team}</span>
                  <span className={cn(
                    "shrink-0 tabular-nums font-medium",
                    compact ? "text-[11px]" : "text-xs",
                    idx === 0 ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {pick.percentage}%
                  </span>
                  {breakdown?.isOfficial && pick.team === breakdown.officialWinner && (
                    <CircleCheck className={cn("shrink-0 text-teal", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
                  )}
                </div>
              ))}
              {remaining > 0 && (
                <div className={cn(
                  "text-muted-foreground text-right pr-1.5",
                  compact ? "text-[10px]" : "text-xs"
                )}>
                  +{remaining} more
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Check if this game has a known winner and get its metadata
  const winnerMetadata = game.id < knownWinnersWithMetadata.length ? knownWinnersWithMetadata[game.id] : null;
  const hasKnownWinner = !!winnerMetadata && winnerMetadata.team === game.winner;
  const isOfficialWinner = hasKnownWinner && winnerMetadata.source === WinnerSource.OFFICIAL;
  const renderTeam = (team: string | undefined, slot: 'A' | 'B') => {
    if (!team) return null;

    const isWinner = game.winner === team;
    const isHovered = hoveredTeamId === `${game.id}-${slot}` && !isOfficialWinner;
    const isOfficialWinnerTeam = isOfficialWinner && isWinner;
    const isUserPickWinner = isWinner && !isOfficialWinner;

    const canReset =
      game.round > 0 &&
      team &&
      (!isOfficialWinner || !isWinner) &&
      !isTeamFromOfficialKnownWinnerPath(team, game.id, knownWinnersWithMetadata, games);

    return (
      <div
        className={cn(
          'relative flex items-center rounded-md border transition-colors',
          compact ? `gap-1 px-1.5 py-0.5 ${compactTextClass || 'text-[14px]'}` : 'gap-2 px-2.5 py-1.5 text-sm',
          isOfficialWinnerTeam
            ? 'border-teal bg-teal/10 cursor-default'
            : isUserPickWinner
              ? 'border-primary bg-primary/10 cursor-pointer'
              : 'border-transparent cursor-pointer',
          isHovered && !isOfficialWinnerTeam && 'bg-accent'
        )}
        onClick={() => team && !isOfficialWinner && handleTeamSelect(game.id, team)}
        onMouseEnter={() => !isOfficialWinner && setHoveredTeamId(`${game.id}-${slot}`)}
        onMouseLeave={() => setHoveredTeamId(null)}
      >
        <Badge variant="secondary" className={cn(
          "justify-center rounded-sm py-0",
          compact ? "min-w-[22px] px-1 text-xs" : "min-w-[28px] px-1.5 text-xs"
        )}>
          {getTeamSeed(team)}
        </Badge>
        <span className="flex-1 truncate">{team}</span>

        {/* Show lock icon for official winners */}
        {isOfficialWinnerTeam && (
          <Lock className={cn("shrink-0 text-teal", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
        )}

        {/* Show reset button only for teams in rounds beyond the first one
            AND only for non-known winners AND not from known winner path */}
        {canReset && (
          <button
            className={cn(
              "absolute top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/40 transition-colors",
              compact ? "right-1 h-3.5 w-3.5" : "right-1.5 h-4 w-4"
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleResetTeam(game.id, team);
            }}
          >
            <X className={cn(compact ? "h-2.5 w-2.5" : "h-3 w-3")} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={cn("rounded-lg border bg-card", compact ? "p-1" : "p-2.5")} key={game.id}>
      <div className={cn("flex flex-col", compact ? "gap-0.5" : "gap-1")}>
        {renderTeam(game.teamA, 'A')}
        {renderTeam(game.teamB, 'B')}
      </div>
    </div>
  );
};

export default BracketGame;
